import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';

const ACTION_CONFIG = {
  ALL:             { label: 'All Actions',       icon: '🌐', color: '#64748B', bg: '#F1F5F9' },
  LOGIN:           { label: 'User Login',        icon: '🔑', color: '#16A34A', bg: '#DCFCE7' },
  LOGOUT:          { label: 'User Logout',       icon: '🚪', color: '#475569', bg: '#F1F5F9' },
  VIEW_REPORT:     { label: 'Viewed Report',     icon: '👁️', color: '#2563EB', bg: '#DBEAFE' },
  READ_EXCEL:      { label: 'Read Spreadsheet',  icon: '📊', color: '#0D9488', bg: '#CCFBF1' },
  DOWNLOAD_REPORT: { label: 'Downloaded File',   icon: '📥', color: '#D97706', bg: '#FEF3C7' },
  ACCESS_DENIED:   { label: 'Access Denied',     icon: '🚫', color: '#DC2626', bg: '#FEE2E2' },
  GRANT_ACCESS:    { label: 'Access Granted',    icon: '🛡️', color: '#059669', bg: '#D1FAE5' },
  REVOKE_ACCESS:   { label: 'Access Revoked',    icon: '⛔', color: '#991B1B', bg: '#FFE4E6' },
  CREATE_USER:     { label: 'Created User',      icon: '👤', color: '#7C3AED', bg: '#EDE9FE' },
  UPDATE_USER:     { label: 'Updated User',      icon: '✏️', color: '#4F46E5', bg: '#EEF2FF' },
  DELETE_USER:     { label: 'Deactivated User',  icon: '🗑️', color: '#E11D48', bg: '#FFE4E6' },
  SYNC_ONEDRIVE:   { label: 'OneDrive Sync',     icon: '🔄', color: '#0284C7', bg: '#E0F2FE' },
  CREATE_EMAIL_FLOW:{ label: 'Created Flow',     icon: '⚡', color: '#9333EA', bg: '#F3E8FF' },
  UPDATE_EMAIL_FLOW:{ label: 'Updated Flow',     icon: '⚡', color: '#9333EA', bg: '#F3E8FF' },
  MAP_FOLDER:      { label: 'Mapped Folder',     icon: '📁', color: '#EA580C', bg: '#FFEDD5' },
};

const DATE_PRESETS = [
  { id: 'all', label: 'All Time' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7days', label: 'Last 7 Days' },
  { id: 'last30days', label: 'Last 30 Days' },
];

const LIMIT = 30;

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Filters
  const [selectedUser, setSelectedUser] = useState('ALL');
  const [selectedAction, setSelectedAction] = useState('ALL');
  const [datePreset, setDatePreset] = useState('all');
  const [search, setSearch] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Selected event for forensic inspection modal
  const [inspectEvent, setInspectEvent] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (selectedUser !== 'ALL') params.userId = selectedUser;
      if (selectedAction !== 'ALL') params.action = selectedAction;
      if (datePreset !== 'all') params.datePreset = datePreset;
      if (search.trim()) params.search = search.trim();
      if (customStartDate) params.startDate = customStartDate;
      if (customEndDate) params.endDate = customEndDate;

      const res = await adminApi.auditLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || null);
      if (res.data.availableUsers) {
        setAvailableUsers(res.data.availableUsers);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, selectedUser, selectedAction, datePreset, search, customStartDate, customEndDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = Math.ceil(total / LIMIT);

  const handleExportCsv = () => {
    const params = new URLSearchParams();
    if (selectedUser !== 'ALL') params.append('userId', selectedUser);
    if (selectedAction !== 'ALL') params.append('action', selectedAction);
    if (datePreset !== 'all') params.append('datePreset', datePreset);
    if (search.trim()) params.append('search', search.trim());
    if (customStartDate) params.append('startDate', customStartDate);
    if (customEndDate) params.append('endDate', customEndDate);

    const url = `/api/admin/audit-logs/export?${params.toString()}`;
    window.open(url, '_blank');
    toast.success('Exporting forensic audit logs to CSV...');
  };

  const handleResetFilters = () => {
    setSelectedUser('ALL');
    setSelectedAction('ALL');
    setDatePreset('all');
    setSearch('');
    setCustomStartDate('');
    setCustomEndDate('');
    setPage(1);
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '24px 20px 60px' }}>
      {/* ── Top Hero Card ──────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #091E3A 0%, #111827 50%, #1F2937 100%)',
        borderRadius: '24px',
        padding: '32px',
        color: 'white',
        boxShadow: '0 20px 45px rgba(0, 0, 0, 0.25)',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Ambient Glows */}
        <div style={{
          position: 'absolute', right: '-40px', top: '-40px', width: '250px', height: '250px',
          borderRadius: '50%', background: 'rgba(59, 130, 246, 0.18)', filter: 'blur(50px)'
        }} />
        <div style={{
          position: 'absolute', left: '25%', bottom: '-60px', width: '200px', height: '200px',
          borderRadius: '50%', background: 'rgba(16, 185, 129, 0.15)', filter: 'blur(45px)'
        }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20, position: 'relative', zIndex: 1 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{
                fontSize: '11px', fontWeight: 800, textTransform: 'uppercase',
                background: 'rgba(59, 130, 246, 0.25)', color: '#93C5FD',
                padding: '4px 14px', borderRadius: '99px', letterSpacing: '0.06em',
                border: '1px solid rgba(59, 130, 246, 0.35)'
              }}>
                🛡️ Security & Forensic Audit Hub
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#6EE7B7' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', display: 'inline-block', boxShadow: '0 0 8px #10B981' }} />
                Real-Time Event Tracking Active
              </span>
            </div>

            <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 800, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
              System Audit & User Activity Logs
            </h1>

            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#94A3B8', maxWidth: '780px', lineHeight: 1.6 }}>
              Complete forensic record of every action across the platform. Track user logins, report views, spreadsheet reads,
              file downloads, email flow triggers, and permission changes with full user attribution and IP timestamps.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', fontSize: '12.5px', color: '#64748B' }}>
              <span>👤 <strong>Active Users:</strong> {availableUsers.length} Registered</span>
              <span>🔒 <strong>Access Control:</strong> Administrator Forensic View</span>
              <span>⚡ <strong>Telemetry:</strong> Synchronous Audit Dispatch</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignSelf: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleExportCsv}
              className="btn"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontWeight: 600, gap: 8 }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export Audit CSV
            </button>

            <button
              onClick={loadLogs}
              disabled={loading}
              className="btn btn-primary"
              style={{ background: '#3B82F6', border: 'none', fontWeight: 600, gap: 8 }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}>
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              {loading ? 'Refreshing...' : 'Refresh Logs'}
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI Counter Cards ────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{ background: '#FFFFFF', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            📋 Total Events Recorded
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#0F172A', fontFamily: 'var(--font-display)' }}>
            {total.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#64748B', marginTop: 4 }}>
            In current filter scope
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            🔑 User Logins
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#16A34A', fontFamily: 'var(--font-display)' }}>
            {stats?.loginsCount || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#16A34A', fontWeight: 600, marginTop: 4 }}>
            Authentication sessions
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            👁️ Reports & Sheets Viewed
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#2563EB', fontFamily: 'var(--font-display)' }}>
            {stats?.viewsCount || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#2563EB', fontWeight: 600, marginTop: 4 }}>
            In-app file inspections
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            📥 Files Downloaded
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#D97706', fontFamily: 'var(--font-display)' }}>
            {stats?.downloadsCount || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#D97706', fontWeight: 600, marginTop: 4 }}>
            Spreadsheet & PDF exports
          </div>
        </div>

        <div style={{ background: '#FFFFFF', padding: '20px', borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            👥 Active Users
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#7C3AED', fontFamily: 'var(--font-display)' }}>
            {stats?.uniqueUsersActive || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#7C3AED', fontWeight: 600, marginTop: 4 }}>
            Distinct staff active in window
          </div>
        </div>
      </div>

      {/* ── Universal Multi-Dimensional Filter Control Bar ────────────────── */}
      <div style={{
        background: '#FFFFFF',
        padding: '22px',
        borderRadius: '16px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {/* Row 1: Search & Dropdowns */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Search Box */}
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '420px' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }}>
              🔍
            </span>
            <input
              type="text"
              className="input"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by user, email, action details, IP..."
              style={{ paddingLeft: '40px', width: '100%', height: '42px', fontSize: '13.5px', borderRadius: '10px' }}
            />
            {search && (
              <button
                onClick={() => { setSearch(''); setPage(1); }}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' }}
              >
                ✕
              </button>
            )}
          </div>

          {/* User Filter Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#475569' }}>👤 User:</span>
            <select
              className="input"
              value={selectedUser}
              onChange={e => { setSelectedUser(e.target.value); setPage(1); }}
              style={{ height: '42px', fontSize: '13px', borderRadius: '10px', minWidth: '220px', padding: '0 12px' }}
            >
              <option value="ALL">🌐 All Users ({availableUsers.length})</option>
              {availableUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email}) — [{u.role}]
                </option>
              ))}
            </select>
          </div>

          {/* Action Filter Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#475569' }}>⚡ Action:</span>
            <select
              className="input"
              value={selectedAction}
              onChange={e => { setSelectedAction(e.target.value); setPage(1); }}
              style={{ height: '42px', fontSize: '13px', borderRadius: '10px', minWidth: '180px', padding: '0 12px' }}
            >
              <option value="ALL">All Actions</option>
              {Object.entries(ACTION_CONFIG).filter(([k]) => k !== 'ALL').map(([k, v]) => (
                <option key={k} value={k}>
                  {v.icon} {v.label}
                </option>
              ))}
            </select>
          </div>

          {/* Reset Filters */}
          {(selectedUser !== 'ALL' || selectedAction !== 'ALL' || datePreset !== 'all' || search) && (
            <button
              onClick={handleResetFilters}
              className="btn btn-sm"
              style={{ background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA', fontWeight: 700, padding: '8px 14px' }}
            >
              ✕ Reset Filters
            </button>
          )}
        </div>

        {/* Row 2: Date Preset Pills & Custom Range */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #F1F5F9', paddingTop: '14px' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748B', marginRight: 4 }}>
              📅 Time Range:
            </span>
            {DATE_PRESETS.map(preset => {
              const isSelected = datePreset === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => { setDatePreset(preset.id); setCustomStartDate(''); setCustomEndDate(''); setPage(1); }}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '8px',
                    border: isSelected ? '1px solid #2563EB' : '1px solid #E2E8F0',
                    background: isSelected ? '#EFF6FF' : '#F8FAFC',
                    color: isSelected ? '#1D4ED8' : '#475569',
                    fontSize: '12px',
                    fontWeight: isSelected ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Inputs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px' }}>
            <span style={{ color: '#64748B', fontWeight: 600 }}>Custom:</span>
            <input
              type="date"
              className="input"
              value={customStartDate}
              onChange={e => { setCustomStartDate(e.target.value); setDatePreset('custom'); setPage(1); }}
              style={{ height: '32px', fontSize: '12px', padding: '0 8px', borderRadius: '6px' }}
            />
            <span style={{ color: '#94A3B8' }}>➔</span>
            <input
              type="date"
              className="input"
              value={customEndDate}
              onChange={e => { setCustomEndDate(e.target.value); setDatePreset('custom'); setPage(1); }}
              style={{ height: '32px', fontSize: '12px', padding: '0 8px', borderRadius: '6px' }}
            />
          </div>
        </div>
      </div>

      {/* ── Audit Logs Master Forensic Table ────────────────────────────── */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        border: '1px solid #E2E8F0',
        boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: 12, animation: 'spin 1.5s linear infinite' }}>⏳</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Loading Forensic Audit Stream...</div>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: 10 }}>🔍</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>No audit events found matching filters</div>
            <div style={{ fontSize: '13px', color: '#64748B', marginTop: 4 }}>Try clearing search keywords or selecting a broader date range.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#475569', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '14px 16px', width: '50px' }}>#</th>
                  <th style={{ padding: '14px 16px' }}>Timestamp</th>
                  <th style={{ padding: '14px 16px' }}>User & Role</th>
                  <th style={{ padding: '14px 16px' }}>Action</th>
                  <th style={{ padding: '14px 16px' }}>Target Report / Activity Details</th>
                  <th style={{ padding: '14px 16px' }}>Client IP</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right' }}>Forensics</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => {
                  const cfg = ACTION_CONFIG[log.action] || { label: log.action, icon: '📌', color: '#475569', bg: '#F1F5F9' };
                  const userRole = log.user?.role || 'VIEWER';
                  const roleBadgeBg = userRole === 'ADMIN' ? '#FEE2E2' : userRole === 'MANAGER' ? '#FEF3C7' : userRole === 'CLIENT' ? '#FFEDD5' : '#EFF6FF';
                  const roleBadgeColor = userRole === 'ADMIN' ? '#991B1B' : userRole === 'MANAGER' ? '#92400E' : userRole === 'CLIENT' ? '#9A3412' : '#1E40AF';

                  return (
                    <tr
                      key={log.id}
                      style={{
                        borderBottom: '1px solid #F1F5F9',
                        transition: 'background 0.15s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => setInspectEvent(log)}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* Index */}
                      <td style={{ padding: '14px 16px', color: '#94A3B8', fontWeight: 600 }}>
                        {(page - 1) * LIMIT + idx + 1}
                      </td>

                      {/* Timestamp */}
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '12.5px' }}>
                          {log.createdAt ? format(new Date(log.createdAt), 'dd MMM yyyy, HH:mm:ss') : '—'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: 2 }}>
                          {log.createdAt ? formatDistanceToNow(new Date(log.createdAt), { addSuffix: true }) : ''}
                        </div>
                      </td>

                      {/* User & Role */}
                      <td style={{ padding: '14px 16px', minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: '18px' }}>
                            {userRole === 'ADMIN' ? '👑' : userRole === 'MANAGER' ? '👔' : userRole === 'CLIENT' ? '🏢' : '👤'}
                          </span>
                          <div>
                            <div style={{ fontWeight: 700, color: '#0F172A' }}>
                              {log.user?.name || 'System User'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                              <span style={{ fontSize: '11.5px', color: '#64748B' }}>
                                {log.user?.email}
                              </span>
                              <span style={{
                                fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
                                background: roleBadgeBg, color: roleBadgeColor,
                                padding: '1px 6px', borderRadius: '4px'
                              }}>
                                {userRole}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Action Pill */}
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: cfg.bg, color: cfg.color, fontWeight: 700,
                          fontSize: '12px', padding: '4px 10px', borderRadius: '8px',
                          border: `1px solid ${cfg.color}30`
                        }}>
                          <span>{cfg.icon}</span>
                          <span>{cfg.label}</span>
                        </span>
                      </td>

                      {/* Target / Details */}
                      <td style={{ padding: '14px 16px', maxWidth: '380px' }}>
                        <div style={{ color: '#1E293B', fontWeight: 600, wordBreak: 'break-word' }}>
                          {log.report?.name || log.details || '—'}
                        </div>
                      </td>

                      {/* IP Address */}
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontFamily: 'monospace', fontSize: '11.5px',
                          background: '#F1F5F9', color: '#475569',
                          padding: '3px 8px', borderRadius: '6px'
                        }}>
                          {log.ipAddress || '127.0.0.1'}
                        </span>
                      </td>

                      {/* Forensics Action */}
                      <td style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-sm"
                          onClick={(e) => { e.stopPropagation(); setInspectEvent(log); }}
                          style={{ background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', fontWeight: 600, padding: '4px 10px', fontSize: '11.5px' }}
                        >
                          🔍 Inspect
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination Footer ───────────────────────────────────────── */}
        <div style={{
          padding: '16px 20px',
          background: '#F8FAFC',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}>
          <div style={{ fontSize: '12.5px', color: '#64748B' }}>
            Showing <strong>{logs.length > 0 ? (page - 1) * LIMIT + 1 : 0}</strong> to <strong>{Math.min(page * LIMIT, total)}</strong> of <strong>{total.toLocaleString()}</strong> events
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              style={{ background: '#FFFFFF', border: '1px solid #CBD5E1', color: '#0F172A', fontWeight: 600 }}
            >
              ← Previous
            </button>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155', padding: '0 8px' }}>
              Page {page} of {totalPages || 1}
            </span>
            <button
              className="btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ background: '#FFFFFF', border: '1px solid #CBD5E1', color: '#0F172A', fontWeight: 600 }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* ── Forensic Event Inspection Modal ─────────────────────────────── */}
      {inspectEvent && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: '#FFFFFF', borderRadius: '20px', width: '100%', maxWidth: '600px',
            padding: '28px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '24px' }}>🛡️</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>
                    Forensic Audit Event Details
                  </h3>
                  <div style={{ fontSize: '12px', color: '#64748B' }}>
                    Event ID: <code>{inspectEvent.id}</code>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setInspectEvent(null)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#94A3B8' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#F8FAFC', padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                  Action Performed
                </div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#0F172A' }}>
                  {ACTION_CONFIG[inspectEvent.action]?.icon} {ACTION_CONFIG[inspectEvent.action]?.label || inspectEvent.action}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                    User Attribution
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>
                    {inspectEvent.user?.name || '—'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#2563EB' }}>
                    {inspectEvent.user?.email}
                  </div>
                </div>

                <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                    Timestamp
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>
                    {inspectEvent.createdAt ? format(new Date(inspectEvent.createdAt), 'dd MMM yyyy, HH:mm:ss') : '—'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#059669' }}>
                    {inspectEvent.createdAt ? formatDistanceToNow(new Date(inspectEvent.createdAt), { addSuffix: true }) : ''}
                  </div>
                </div>
              </div>

              <div style={{ background: '#F8FAFC', padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                  Details & Target
                </div>
                <div style={{ fontSize: '13px', color: '#1E293B', fontWeight: 600, wordBreak: 'break-word' }}>
                  {inspectEvent.report?.name || inspectEvent.details || '—'}
                </div>
              </div>

              <div style={{ background: '#F8FAFC', padding: '14px', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: 4 }}>
                  Network & Client Metadata
                </div>
                <div style={{ fontSize: '12.5px', color: '#334155', fontFamily: 'monospace', marginBottom: 4 }}>
                  <strong>IP Address:</strong> {inspectEvent.ipAddress || '127.0.0.1'}
                </div>
                <div style={{ fontSize: '11.5px', color: '#64748B', wordBreak: 'break-all' }}>
                  <strong>User Agent:</strong> {inspectEvent.userAgent || 'Mozilla/5.0'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                className="btn btn-primary"
                onClick={() => setInspectEvent(null)}
                style={{ background: '#0F172A', border: 'none', fontWeight: 600 }}
              >
                Close Inspection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
